import { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from './Icon';
import { getAccessToken, isSecretaryContextUser } from '../authStorage';
import { fetchSecretaryUsers, updateSecretaryUserPermissions } from '../secretaryUsersApi';
import { useLanguage } from '../i18n/language';
import { useT } from '../i18n/translations';
import './BranchesPage.css';
import './Modal.css';
import './PermissionsPage.css';

const MODULES = [
  { id: 'dashboard',         labelKey: 'common.dashboard',    icon: 'dashboard', actions: [{ id: 'view_stats', labelKey: 'common.view' }], uiItems: [{ id: 'recent_table', label: 'Recent decisions table' }] },
  { id: 'users',             labelKey: 'common.users',        icon: 'user',      actions: [{ id: 'create', labelKey: 'common.add' }], uiItems: [{ id: 'create_form', label: 'User create form' }] },
  { id: 'departments',       labelKey: 'departments.title',   icon: 'board',     actions: [{ id: 'list', labelKey: 'common.view' }, { id: 'create', labelKey: 'common.add' }, { id: 'edit', labelKey: 'common.edit' }], uiItems: [{ id: 'hierarchy', label: 'Hierarchy' }] },
  { id: 'operational_plans', labelKey: 'plans.title',         icon: 'report',    actions: [{ id: 'list', labelKey: 'common.view' }, { id: 'create', labelKey: 'common.add' }, { id: 'edit', labelKey: 'common.edit' }], uiItems: [{ id: 'document_form', label: 'Plan document' }] },
  { id: 'branches',          labelKey: 'branches.title',      icon: 'branch',    actions: [{ id: 'list', labelKey: 'common.view' }, { id: 'create', labelKey: 'common.add' }, { id: 'edit', labelKey: 'common.edit' }, { id: 'delete', labelKey: 'common.delete' }], uiItems: [{ id: 'btn_add', label: 'Add branch button' }] },
  { id: 'board',             labelKey: 'board.title',         icon: 'board',     actions: [{ id: 'list', labelKey: 'common.view' }, { id: 'create', labelKey: 'common.add' }, { id: 'edit', labelKey: 'common.edit' }], uiItems: [{ id: 'status_toggle', label: 'Status toggle' }] },
  { id: 'unified_matrix',    labelKey: 'nav.unifiedMatrix',   icon: 'report',    actions: [{ id: 'list', labelKey: 'common.view' }, { id: 'edit', labelKey: 'common.edit' }], uiItems: [{ id: 'edit_btn', label: 'Edit button' }] },
  { id: 'committees',        labelKey: 'committees.title',    icon: 'board',     actions: [{ id: 'list', labelKey: 'common.view' }, { id: 'create', labelKey: 'common.add' }, { id: 'edit', labelKey: 'common.edit' }], uiItems: [{ id: 'members_editor', label: 'Members editor' }] },
  { id: 'shareholders',      labelKey: 'shareholders.title',  icon: 'money',     actions: [{ id: 'list', labelKey: 'common.view' }, { id: 'create', labelKey: 'common.add' }, { id: 'edit', labelKey: 'common.edit' }], uiItems: [{ id: 'ownership_col', label: 'Ownership column' }] },
  { id: 'decisions',         labelKey: 'common.decisions',    icon: 'decisions', actions: [{ id: 'list', labelKey: 'common.view' }, { id: 'create', labelKey: 'common.create' }, { id: 'edit', labelKey: 'common.edit' }], uiItems: [{ id: 'deadline_field', label: 'Deadline field' }] },
  { id: 'decision_types',    labelKey: 'decisions.allTypes',  icon: 'decisions', actions: [{ id: 'list', labelKey: 'common.view' }, { id: 'create', labelKey: 'common.add' }, { id: 'edit', labelKey: 'common.edit' }], uiItems: [{ id: 'branch_filter', label: 'Branch filter' }] },
  { id: 'meetings',          labelKey: 'common.meetings',     icon: 'calendar',  actions: [{ id: 'list', labelKey: 'common.view' }, { id: 'create', labelKey: 'common.add' }, { id: 'edit', labelKey: 'common.edit' }, { id: 'delete', labelKey: 'common.delete' }], uiItems: [{ id: 'invitees_selector', label: 'Invitees selector' }] },
  { id: 'nominations',       labelKey: 'nominations.title',   icon: 'nominations', actions: [{ id: 'list', labelKey: 'common.view' }, { id: 'approve', labelKey: 'nominations.approve' }], uiItems: [{ id: 'notes_field', label: 'Notes field' }] },
  { id: 'permissions',       labelKey: 'permissions.title',   icon: 'lock',      actions: [{ id: 'manage', labelKey: 'permissions.save' }], uiItems: [{ id: 'matrix', label: 'Permissions matrix' }] },
];

function emptyPermissions() {
  const o = {};
  MODULES.forEach((m) => {
    o[m.id] = { pageAccess: false, actions: {}, ui: {} };
    m.actions.forEach((a) => { o[m.id].actions[a.id] = false; });
    m.uiItems.forEach((u) => { o[m.id].ui[u.id] = false; });
  });
  return o;
}

function normalizePermissions(raw) {
  const base = emptyPermissions();
  if (!raw || typeof raw !== 'object') return base;
  MODULES.forEach((m) => {
    const node = raw[m.id] || {};
    base[m.id].pageAccess = !!node.pageAccess;
    m.actions.forEach((a) => { base[m.id].actions[a.id] = !!node.actions?.[a.id]; });
    m.uiItems.forEach((u) => { base[m.id].ui[u.id] = !!node.ui?.[u.id]; });
  });
  return base;
}

export default function PermissionsPage() {
  const { direction } = useLanguage();
  const t = useT();
  const navigate = useNavigate();
  const [accessDenied, setAccessDenied] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [expandedModule, setExpandedModule] = useState(MODULES[0].id);
  const [permissionsByUser, setPermissionsByUser] = useState({});
  const [toast, setToast] = useState(null);

  useEffect(() => {
    if (!getAccessToken()) {
      navigate('/login', { replace: true });
      return;
    }
    if (!isSecretaryContextUser()) {
      setAccessDenied(true);
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const rows = await fetchSecretaryUsers();
        setUsers(rows);
        if (rows.length > 0) setSelectedUserId(rows[0].id);
        const map = {};
        rows.forEach((u) => { map[u.id] = normalizePermissions(u.permissions); });
        setPermissionsByUser(map);
      } catch (e) {
        setLoadError(e instanceof Error ? e.message : t('permissions.loadFailed'));
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedUser = users.find((u) => u.id === selectedUserId);
  const permissions = permissionsByUser[selectedUserId] ?? emptyPermissions();

  const filteredUsers = useMemo(() => {
    const q = userSearch.trim();
    if (!q) return users;
    return users.filter((u) =>
      (u.fullName || '').includes(q) ||
      u.userName.includes(q) ||
      (u.email || '').includes(q) ||
      (u.phone || '').includes(q),
    );
  }, [users, userSearch]);

  const mutate = (moduleId, cb) => {
    setPermissionsByUser((prev) => {
      const userPerm = prev[selectedUserId] ? { ...prev[selectedUserId] } : emptyPermissions();
      const modulePerm = { ...userPerm[moduleId], actions: { ...userPerm[moduleId].actions }, ui: { ...userPerm[moduleId].ui } };
      cb(modulePerm);
      userPerm[moduleId] = modulePerm;
      return { ...prev, [selectedUserId]: userPerm };
    });
  };

  const handleSave = async () => {
    if (!selectedUserId) return;
    try {
      await updateSecretaryUserPermissions(selectedUserId, permissionsByUser[selectedUserId] ?? emptyPermissions());
      setToast({ msg: t('permissions.savedOk'), type: 'ok' });
      setTimeout(() => setToast(null), 3000);
    } catch (e) {
      setToast({ msg: e instanceof Error ? e.message : t('permissions.saveFailed'), type: 'err' });
      setTimeout(() => setToast(null), 3500);
    }
  };

  const countGranted = (modId) => {
    const p = permissions[modId];
    if (!p?.pageAccess) return 0;
    return 1 + Object.values(p.actions).filter(Boolean).length + Object.values(p.ui).filter(Boolean).length;
  };

  if (accessDenied) {
    return (
      <div className="page" dir={direction}>
        <div className="page__topbar">
          <div className="page__topbar-left">
            <span className="page__topbar-title">{t('permissions.title')}</span>
          </div>
        </div>
        <p style={{ padding: 24, color: '#78909c', fontFamily: 'Cairo, sans-serif' }}>{t('common.noPerm')}</p>
      </div>
    );
  }

  return (
    <div className="page perm-page" dir={direction}>
      {toast && <div className={`perm-toast perm-toast--${toast.type === 'ok' ? 'ok' : 'err'}`}>{toast.msg}</div>}
      <div className="list-page-card list-page-card--unified">
        <header className="list-page-card__header">
          <div className="list-page-card__titles">
            <span className="list-page-card__title"><Icon name="lock" size={18} color="#00334a" style={{ verticalAlign: 'middle', marginLeft: 6 }} />{t('permissions.title')}</span>
            <span className="list-page-card__sub">{t('permissions.subtitle')}</span>
          </div>
          <div className="list-page-card__actions">
            <button type="button" className="btn btn--primary" onClick={handleSave} disabled={!selectedUserId}>{t('permissions.save')}</button>
          </div>
        </header>

        {loadError && <div className="list-page-card__banner list-page-card__banner--error">{loadError}</div>}
        {loading && <div className="list-page-card__loading">{t('permissions.loading')}</div>}

        {!loading && (
          <div className="perm-layout">
          <aside className="perm-users">
            <div className="perm-users__head">
              <span className="perm-users__title">{t('permissions.selectUser')}</span>
              <div className="perm-users__search">
                <Icon name="search" size={14} color="#90a4ae" />
                <input type="text" placeholder={t('permissions.search')} value={userSearch} onChange={(e) => setUserSearch(e.target.value)} />
              </div>
            </div>
            <ul className="perm-users__list">
              {filteredUsers.map((u) => (
                <li key={u.id}>
                  <button type="button" className={`perm-user-card${selectedUserId === u.id ? ' perm-user-card--active' : ''}`} onClick={() => setSelectedUserId(u.id)}>
                    <div className="perm-user-card__avatar">{(u.fullName || u.userName || '?')[0]}</div>
                    <div className="perm-user-card__body">
                      <div className="perm-user-card__name">{u.fullName || '—'}</div>
                      <div className="perm-user-card__meta">{u.userName}</div>
                      <div className="perm-user-card__email">{[u.email, u.phone].filter(Boolean).join(' · ') || '—'}</div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          </aside>

          <section className="perm-matrix">
            {selectedUser && (
              <div className="perm-matrix__banner">
                <div className="perm-matrix__banner-icon"><Icon name="user" size={22} color="#fff" /></div>
                <div>
                  <div className="perm-matrix__banner-name">{selectedUser.fullName || '—'}</div>
                  <div className="perm-matrix__banner-sub">{t('auth.username')}: {selectedUser.userName}</div>
                </div>
              </div>
            )}

            <div className="perm-accordions">
              {MODULES.map((mod) => {
                const p = permissions[mod.id];
                const open = expandedModule === mod.id;
                return (
                  <div key={mod.id} className={`perm-acc${open ? ' perm-acc--open' : ''}`}>
                    <button type="button" className="perm-acc__head" onClick={() => setExpandedModule(open ? '' : mod.id)}>
                      <span className="perm-acc__head-left">
                        <span className="perm-acc__icon"><Icon name={mod.icon} size={16} color="#fff" /></span>
                        <span className="perm-acc__title">{t(mod.labelKey)}</span>
                        <span className="perm-acc__count">{countGranted(mod.id)} {t('permissions.enabled')}</span>
                      </span>
                      <Icon name={open ? 'chevron-up' : 'chevron-down'} size={14} color="#78909c" />
                    </button>
                    {open && (
                      <div className="perm-acc__body">
                        <div className="perm-row perm-row--page">
                          <div className="perm-row__label">{t('permissions.pageAccess')}</div>
                          <label className="perm-switch">
                            <input type="checkbox" checked={p.pageAccess} onChange={(e) => mutate(mod.id, (m) => { m.pageAccess = e.target.checked; })} />
                            <span className="perm-switch__slider" />
                          </label>
                        </div>
                        <div className="perm-sub">
                          <div className="perm-sub__title">{t('permissions.actions')}</div>
                          <div className="perm-chips">
                            {mod.actions.map((a) => (
                              <label key={a.id} className={`perm-chip${p.actions[a.id] ? ' perm-chip--on' : ''}`}>
                                <input type="checkbox" checked={p.actions[a.id]} disabled={!p.pageAccess} onChange={(e) => mutate(mod.id, (m) => { m.actions[a.id] = e.target.checked; })} />
                                <span>{t(a.labelKey)}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                        <div className="perm-sub">
                          <div className="perm-sub__title">{t('permissions.uiItems')}</div>
                          <div className="perm-chips perm-chips--ui">
                            {mod.uiItems.map((ui) => (
                              <label key={ui.id} className={`perm-chip perm-chip--ui${p.ui[ui.id] ? ' perm-chip--on' : ''}`}>
                                <input type="checkbox" checked={p.ui[ui.id]} disabled={!p.pageAccess} onChange={(e) => mutate(mod.id, (m) => { m.ui[ui.id] = e.target.checked; })} />
                                <span>{ui.labelKey ? t(ui.labelKey) : ui.label}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        </div>
        )}
      </div>
    </div>
  );
}
